import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, BatteryCharging, Bolt, Check, PartyPopper, Play, Sun } from "lucide-react";
import { CtaBand, EASE, IMG, Line, Marquee, Reveal } from "@/components/shared";
import { useLang } from "@/i18n";

const HeroMedia = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotateX = useSpring(useTransform(my, [0, 1], [6, -6]), { stiffness: 120, damping: 16 });
  const rotateY = useSpring(useTransform(mx, [0, 1], [-8, 8]), { stiffness: 120, damping: 16 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const onMove = (event) => {
    const rect = ref.current.getBoundingClientRect();
    const px = (event.clientX - rect.left) / rect.width;
    const py = (event.clientY - rect.top) / rect.height;
    mx.set(px); my.set(py);
    ref.current.style.setProperty("--sx", `${px * 100}%`);
    ref.current.style.setProperty("--sy", `${py * 100}%`);
  };
  return (
    <div className="hero-media" ref={ref} onMouseMove={onMove} onMouseLeave={() => { mx.set(0.5); my.set(0.5); }} data-testid="hero-energy-diagram" aria-label="AsaanCharger1 portable battery module">
      <motion.div className="media-frame" style={{ rotateX, rotateY, y }}>
        <img src={IMG.hero} alt="AsaanCharger1 portable battery module" />
        <div className="media-shade" />
        <div className="media-spot" />
        <motion.div className="media-wipe" initial={{ scaleY: 1 }} animate={{ scaleY: 0 }} transition={{ duration: 1.15, delay: 0.55, ease: [0.76, 0, 0.24, 1] }} />
        <div className="media-tag"><span className="pulse-dot" /> {t("mediaTag")}</div>
      </motion.div>
      <div className="media-chip chip-top"><Sun size={15} /><span>{t("chip1")}</span></div>
      <div className="media-chip chip-bottom"><BatteryCharging size={15} /><span>{t("chip2")}</span></div>
    </div>
  );
};

export default function Home() {
  const { t, lang } = useLang();
  useEffect(() => { document.title = "AsaanCharge | Clean power, made easy."; }, []);
  const problems = [t("prob1"), t("prob2"), t("prob3")];
  const uses = [
    { icon: Bolt, img: IMG.ev, tag: t("tagFlagship"), title: t("use1T"), copy: t("use1P") },
    { icon: PartyPopper, img: IMG.events, title: t("use2T"), copy: t("use2P") },
    { icon: BatteryCharging, img: IMG.module, title: t("use3T"), copy: t("use3P") },
  ];
  const paths = [
    { number: "01", title: t("path1T"), copy: t("path1P"), cta: t("path1C") },
    { number: "02", title: t("path2T"), copy: t("path2P"), cta: t("path2C") },
    { number: "03", title: t("path3T"), copy: t("path3P"), cta: t("path3C") },
  ];
  return (
    <>
      <div className="container">
        <section className="hero" id="top">
          <div className="hero-copy">
            <motion.div className="eyebrow eyebrow-dark" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1, ease: EASE }}><span className="pulse-dot" /> {t("heroEyebrow")}</motion.div>
            <h1><Line delay={0.22}>{t("heroL1")}</Line><Line delay={0.34}>{t("heroL2")}</Line><Line delay={0.46}><em>{t("heroL3")}</em></Line></h1>
            <motion.p className="hero-sub" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.65, ease: EASE }}>{t("heroSub")}</motion.p>
            <motion.div className="hero-actions" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.78, ease: EASE }}><Link className="button button-primary" to="/company#contact" data-testid="hero-business-button">{t("heroCta1")} <ArrowRight /></Link><Link className="button button-quiet-dark" to="/platform" data-testid="hero-host-button">{t("heroCta2")} <ArrowUpRight /></Link></motion.div>
            <motion.div className="hero-note" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1 }}><span><Check size={14} /> {t("heroNote1")}</span><span><Check size={14} /> {t("heroNote2")}</span></motion.div>
          </div>
          <HeroMedia />
        </section>
      </div>

      <Marquee />

      <section className="stats-section container" data-testid="stats-section">
        <div className="stats-blob" aria-hidden="true" />
        <Reveal><div className="eyebrow">{t("statEyebrow")}</div></Reveal>
        <div className="stats-grid">{[["stat1N", "stat1L"], ["stat2N", "stat2L"], ["stat3N", "stat3L"], ["stat4N", "stat4L"]].map(([nk, lk], index) => <Reveal className="stat" key={nk} delay={index * 0.08} data-testid={`stat-${index + 1}`}><b>{t(nk)}</b><span>{t(lk)}</span></Reveal>)}</div>
      </section>

      <section className="section container" id="problem">
        <Reveal className="section-heading"><div><div className="eyebrow">{t("probEyebrow")}</div><h2>{t("probL1")}<br /><em>{t("probL2")}</em></h2></div><p>{t("probP")}</p></Reveal>
        <div className="problem-grid">{problems.map((problem, index) => <Reveal className="problem-card" key={problem} delay={index * 0.1} y={50} data-testid={`problem-card-${index + 1}`}><span>0{index + 1}</span><h3>{problem}</h3><ArrowUpRight /></Reveal>)}</div>
      </section>

      <section className="section uses-section">
        <div className="container">
          <Reveal className="section-heading"><div><div className="eyebrow">{t("useEyebrow")}</div><h2>{t("useL1")}<br /><em>{t("useL2")}</em></h2></div><p>{t("useP")}</p></Reveal>
          <div className="apps-grid">{uses.map(({ icon: Icon, img, tag, title, copy }, index) => <Reveal className={`app-card ${tag ? "app-flag" : ""}`} key={title} delay={index * 0.1} data-testid={`use-card-${index + 1}`}><div className="app-img"><img src={img} alt={title} />{tag && <span className="app-tag">{tag}</span>}</div><div className="app-body"><h3>{title}</h3><p>{copy}</p></div></Reveal>)}</div>
          <Reveal className="teaser-more" delay={0.15}><Link to="/platform" data-testid="teaser-platform-link">{t("teaserMore")} <ArrowRight /></Link></Reveal>
        </div>
      </section>

      <section className="video-section">
        <div className="container">
          <Reveal className="section-heading"><div><div className="eyebrow eyebrow-dark">{t("vidEyebrow")}</div><h2>{t("vidL1")} <em>{t("vidL2")}</em></h2></div><p>{t("vidP")}</p></Reveal>
          <Reveal className="concept-frame">
            <video key={lang} src={lang === "ur" ? "/videos/concept-ur.mp4" : "/videos/concept-en.mp4"} poster={IMG.ev} controls playsInline preload="metadata" data-testid="concept-video" />
            <span className="video-label"><Play size={12} fill="currentColor" /> {lang === "ur" ? t("vidUr") : t("vidEn")} · {t("vidFor")}</span>
          </Reveal>
        </div>
      </section>

      <section className="ev-spotlight container">
        <Reveal className="ev-card" data-testid="audience-ev-card">
          <img className="ev-bg" src={IMG.ev} alt="" />
          <div className="ev-shade" />
          <div className="ev-copy">
            <span className="card-kicker">{t("evKicker")}</span>
            <h2>{t("evL1")} <em>{t("evL2")}</em></h2>
            <p>{t("evP")}</p>
            <Link to="/company#contact" data-testid="audience-ev-link">{t("evLink")} <ArrowRight /></Link>
          </div>
        </Reveal>
      </section>

      <section className="section map-section container">
        <Reveal className="section-heading"><div><div className="eyebrow">{t("mapEyebrow")}</div><h2>{t("mapL1")}<br /><em>{t("mapL2")}</em></h2></div><p>{t("mapP")}</p></Reveal>
        <Reveal className="map-frame" delay={0.1} data-testid="service-map">
          <iframe title="AsaanCharge service area — Karachi" src="https://maps.google.com/maps?q=Karachi%2C%20Pakistan&z=11&output=embed" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
        </Reveal>
      </section>

      <section className="section involve-section">
        <div className="container">
          <Reveal><div className="eyebrow">{t("invEyebrow")}</div><h2 className="involve-title">{t("invL1")}<br /><em>{t("invL2")}</em></h2></Reveal>
          <div className="involve-grid">{paths.map((path, index) => <Reveal className="involve-card" key={path.title} delay={index * 0.1} data-testid={`path-card-${index + 1}`}><span>{path.number}</span><h3>{path.title}</h3><p>{path.copy}</p><Link to="/company#contact" data-testid={`path-link-${index + 1}`}>{path.cta} <ArrowRight /></Link></Reveal>)}</div>
        </div>
      </section>

      <section className="status-section container" data-testid="status-section">
        <span className="status-word" aria-hidden="true">KARACHI</span>
        <Reveal className="status-content"><div className="status-mark"><span className="pulse-dot" /><span>{t("statusMark")}</span></div><h2>{t("statusL1")}<br /><em>{t("statusL2")}</em></h2><p>{t("statusP")}</p></Reveal>
      </section>

      <CtaBand />
    </>
  );
}
